// src/components/heatmap/HeatmapLegend.tsx
/// Canonical heatmap legend used by both widget and full map. Legacy 'Legend.tsx' kept only for fallback.

import React, { useMemo } from "react";
import { useLgsLayerEnabled, useHeatmapStore } from "@/lib/heatmapStore";
import lgsPng from "@/assets/lgs.png"; // adjust if different

export type Props = {
  className?: string;
  /** Show a chip indicating Local Game Stores (red dot) */
  showLgs?: boolean;
  /** Smaller paddings/font for tight spaces */
  compact?: boolean;
  /** Hide the Local Game Store label */
  hideLgsLabel?: boolean;
};

/**
 * Keep these bands in sync with:
 * - heat aggregation thresholds in heatmapStore (≤5, ≤10, ≤20, ≤40, >40)
 * - gradient stops in LeafletHeatLayer (amber/orange → red)
 */
const RAW_BANDS: Array<{ label: string; color: string }> = [
  { label: "0 – 5 users",  color: "#fde68a" }, // amber-200
  { label: "5 – 10",       color: "#fb923c" }, // orange-400
  { label: "10 – 20",      color: "#ef4444" }, // red-500
  { label: "20 – 40",      color: "#b91c1c" }, // red-700
  { label: "40+",          color: "#7f1d1d" }, // red-900-ish (darker cap)
];

function HeatmapLegendImpl({ className = "", showLgs = true, compact = false, hideLgsLabel = false }: Props) {
  const bands = useMemo(() => RAW_BANDS, []);
  const lgsEnabled = useLgsLayerEnabled();
  const toggle = useHeatmapStore((s) => s.toggleLgsLayer);

  const chipPad = compact ? "px-1.5 py-0.5" : "px-2 py-1";
  const dotSize = compact ? "h-2.5 w-2.5" : "h-3 w-3";
  const textSize = compact ? "text-[10px]" : "text-[11px]";

  return (
    <div
      role="group"
      aria-label="Heatmap legend"
      className={`mt-2 flex flex-wrap gap-2 leading-tight select-none ${textSize} ${className}`}
    >
      {bands.map((b) => (
        <div
          key={b.label}
          className={`flex items-center gap-1 rounded bg-black/50 border border-white/10 ${chipPad}`}
          title={b.label}
        >
          <span
            aria-hidden="true"
            className={`inline-block ${dotSize} rounded-sm ring-1 ring-black/40`}
            style={{ background: b.color }}
          />
          <span className="text-white/80">{b.label}</span>
        </div>
      ))}

      {showLgs && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggle}
            className={`h-5 w-5 rounded flex items-center justify-center text-[9px] font-semibold border transition-colors ${
              lgsEnabled
                ? "bg-amber-400/90 text-black border-amber-300 shadow-inner"
                : "bg-neutral-700/60 text-neutral-300 border-neutral-500"
            } hover:brightness-110`}
            title={lgsEnabled ? "Hide LGS markers" : "Show LGS markers"}
          >
            LGS
          </button>
        </div>
      )}
    </div>
  );
}

// Export both ways to prevent runtime import mismatches (minified React error #185)
export const HeatmapLegend = HeatmapLegendImpl;
export default HeatmapLegendImpl;

